import { Component, Input, Output, EventEmitter, signal, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { Feedback } from '../../../core/services/satisfaction.service';
import { FeedbackSelectionService } from '../../../core/services/feedback-selection.service';
import { TimezoneService } from '../../../core/services/timezone.service';
import { formatInTimeZone } from 'date-fns-tz';

@Component({
  selector: 'app-feedbacks-table',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './feedbacks-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./feedbacks-table.component.scss']
})
export class FeedbacksTableComponent {
  private selectionService = inject(FeedbackSelectionService);
  private timezone = inject(TimezoneService);

  @Input() feedbacks: Feedback[] = [];
  @Input() showPendingOnly = false;
  @Input() sortField = '';
  @Input() sortDirection: 'asc' | 'desc' = 'desc';

  @Output() sort = new EventEmitter<string>();
  @Output() openReplyModal = new EventEmitter<Feedback>();
  @Output() openCallModal = new EventEmitter<Feedback>();
  // DEPRECATED (2026-09-08) : plus émis depuis le template (colonne "Action" retirée) -- voir la
  // note en tête de satisfaction.component.ts.
  @Output() createAction = new EventEmitter<Feedback>();
  @Output() archiveFeedback = new EventEmitter<string>();

  // Getters pour la sélection
  selectedFeedbacks = this.selectionService.selected;

  filteredFeedbacks = computed(() => {
    if (!this.showPendingOnly) return this.feedbacks;
    return this.feedbacks.filter(f => f.wants_reply && f.contact_email && !f.replied_at);
  });

  // Méthodes de sélection
  canSelectFeedback(feedback: Feedback): boolean {
    return this.selectionService.canSelect(feedback);
  }

  isSelected(feedback: Feedback): boolean {
    return this.selectionService.isSelected(feedback);
  }

  toggleSelection(feedback: Feedback) {
    this.selectionService.toggle(feedback);
  }

  isAllSelected(): boolean {
    return this.selectionService.isAllSelected(this.filteredFeedbacks());
  }

  isSomeSelected(): boolean {
    return this.selectionService.isSomeSelected(this.filteredFeedbacks());
  }

  toggleSelectAll() {
    if (this.isAllSelected()) {
      this.selectionService.clear();
    } else {
      this.selectionService.selectAll(this.filteredFeedbacks());
    }
  }

  // Méthodes de tri
  onSort(field: string) {
    this.sort.emit(field);
  }

  // Méthodes d'affichage
  trackByFeedback(index: number, feedback: Feedback): string {
    return feedback.feedback_id;
  }

  getStars(score: number | null): string {
    if (!score) return '—';
    return '★'.repeat(score) + '☆'.repeat(5 - score);
  }

  formatDateShort(dateStr: string): string {
    const tz = this.timezone.getEstablishmentTimezone();
    return formatInTimeZone(dateStr, tz, 'dd/MM/yyyy');
  }

  formatTime(dateStr: string): string {
    const tz = this.timezone.getEstablishmentTimezone();
    const time = formatInTimeZone(dateStr, tz, 'HH:mm');
    return `${time}${this.timezone.getTimezoneHint(new Date(dateStr))}`;
  }

  getWaitTimeLabel(reportedBucket: number): string {
    switch(reportedBucket) {
      case 1: return '< 10 min';
      case 2: return '10-20 min';
      case 3: return '> 20 min';
      default: return '—';
    }
  }

  getWaitTimeClass(reportedBucket: number): string {
    switch(reportedBucket) {
      case 1: return 'wait-fast';
      case 2: return 'wait-medium';
      case 3: return 'wait-slow';
      default: return '';
    }
  }

  onOpenReplyModal(feedback: Feedback) {
    this.openReplyModal.emit(feedback);
  }

  onOpenCallModal(feedback: Feedback) {
    this.openCallModal.emit(feedback);
  }

  onCreateAction(feedback: Feedback) {
    this.createAction.emit(feedback);
  }

  onArchiveFeedback(feedback: Feedback) {
    this.archiveFeedback.emit(feedback.feedback_id);
  }

  getCallTitle(feedback: Feedback): string {
    if (feedback.contact_method === 'PHONE') {
      return 'Patient contacté le ' + this.formatDateShort(feedback.replied_at!);
    } else if (feedback.contact_method === 'CLOSED_WITHOUT_CONTACT') {
      return 'Clôturé sans contact le ' + this.formatDateShort(feedback.replied_at!);
    }
    return 'Contacté le ' + this.formatDateShort(feedback.replied_at!);
  }
}